import { useState, useEffect, useCallback } from "react";

/** Default options for react-intersection-observer's useInView hook. */
export const IN_VIEW_OPTIONS = { triggerOnce: true, threshold: 0.15 };

/**
 * Returns Tailwind classes for a scroll-reveal effect.
 * @param {boolean} visible – whether the element is in view
 */
export const reveal = (visible) =>
  `transition-all duration-700 ease-out ${
    visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
  }`;

/**
 * Hook that returns a 0→1→0 progress value as a section scrolls through the viewport.
 * 0 = section not yet in view (below) OR scrolled past (above), 1 = section centered.
 * Elements assemble as section enters, scatter as it leaves.
 * @param {React.RefObject} ref – ref attached to the section element
 */
export const useScrollProgress = (ref) => {
  const [progress, setProgress] = useState(0);

  const handleScroll = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const windowH = window.innerHeight;
    const sectionH = rect.height;

    // How far the section center is from viewport center (normalized)
    const sectionCenter = rect.top + sectionH / 2;
    const viewportCenter = windowH / 2;
    const distance = Math.abs(sectionCenter - viewportCenter);
    // Normalize: 0 distance = 1 progress, distance >= viewport = 0
    const maxDist = (windowH + sectionH) / 2;
    const raw = 1 - distance / maxDist;
    setProgress(Math.max(0, Math.min(1, raw)));
  }, [ref]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return progress;
};

export const clamp = (value, min = 0, max = 1) =>
  Math.min(max, Math.max(min, value));

export const smoothstep = (start, end, value) => {
  if (start === end) {
    return value >= end ? 1 : 0;
  }

  const normalized = clamp((value - start) / (end - start));

  return normalized * normalized * (3 - 2 * normalized);
};

export const getScrollVisibility = (progress, start = 0.15, end = 0.4) =>
  smoothstep(start, end, progress);
